// TurfAI Workflow Service

import { 
  WorkflowExecution,
  Activity,
  CreateWorkflowExecutionData,
  CreateActivityData,
  ValidationResults,
  GapAnalysis,
  EmployeeInfo,
  StrapiResponse,
  StrapiError
} from './types';
import { turfaiConfig } from './config';
import { authService } from './authService';
import { dmsService } from './dmsService';
import { workflowResolver, ResolvedWorkflow } from './workflowResolver';

export class TurfAIWorkflowService {
  private baseUrl: string;
  private workflowId: string;

  constructor() {
    this.baseUrl = turfaiConfig.dms.apiUrl;
    this.workflowId = turfaiConfig.workflow.id;
  }

  // Activity Management (Workflow Templates)
  async createActivity(data: CreateActivityData): Promise<Activity> {
    try {
      const response = await fetch(`${this.baseUrl}/activities`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ data })
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to create activity: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<Activity>;
      return result.data;
    } catch (error) {
      console.error('Error creating activity:', error);
      throw error;
    }
  }

  async getActivity(activityId: string): Promise<Activity> {
    try {
      const response = await fetch(`${this.baseUrl}/activities/${activityId}`, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to fetch activity: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<Activity>;
      return result.data;
    } catch (error) {
      console.error('Error fetching activity:', error);
      throw error;
    }
  }

  // Workflow Execution
  async executeOnboardingWorkflow(
    collectionId: string, 
    employeeData: EmployeeInfo
  ): Promise<WorkflowExecution> {
    try {
      const executionData: CreateWorkflowExecutionData = {
        activity: this.workflowId,
        inputs: {
          collection_id: collectionId,
          employee_data: employeeData,
          onboarding_config: {
            required_documents: ['Aadhaar Card', 'PAN Card', 'Resume', 'Previous Employment'],
            validation_threshold: 0.7,
            auto_approve_threshold: 0.9,
            enable_parallel_processing: true
          },
          processing_config: {
            enable_ocr: true,
            enable_validation: true,
            enable_scoring: true,
            enable_gap_analysis: true,
            processing_mode: 'batch_classification_extraction'
          }
        }
      };

      const response = await fetch(`${this.baseUrl}/workflow-executions`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ data: executionData })
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to create workflow execution: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<WorkflowExecution>;
      
      // If execution was created, start it
      if (result.data && result.data.id) {
        return await this.startWorkflowExecution(result.data.id);
      }

      return result.data;
    } catch (error) {
      console.error('Error executing onboarding workflow:', error);
      throw error;
    }
  }

  // Batch Classification - Processes CURRENT UPLOAD BATCH only (TurfAI Public API Format)
  // "Batch" = the specific documents uploaded in this session, NOT all collection documents
  async executeBatchClassification(collectionId: string, employeeData: any, options?: { 
    workflowType?: string;
    parallel?: boolean;
    maxConcurrent?: number;
    timeout?: number;
    retry?: boolean;
    promptId?: string;
    promptText?: string;
    specificDocuments?: Array<{
      id: string;
      fileUrl: string;
      title: string;
      mimeType: string;
      fileSize: number;
    }>;
  }): Promise<WorkflowExecution> {
    try {
      // Step 1: Resolve workflow using logical name
      const workflowType = options?.workflowType || 'classify-and-extract';
      
      let resolvedWorkflow: ResolvedWorkflow;
      try {
        resolvedWorkflow = await workflowResolver.resolveWorkflow(workflowType);
      } catch (resolverError) {
        throw new Error(`Failed to resolve workflow '${workflowType}': ${resolverError instanceof Error ? resolverError.message : 'Unknown error'}`);
      }

      // Step 2: Use specific documents if provided, otherwise fetch from collection
      let documents;
      
      if (options?.specificDocuments && options.specificDocuments.length > 0) {
        // Use the current batch (specific documents from this upload session)
        console.log(`🎯 Processing current BATCH of ${options.specificDocuments.length} documents from this upload session`);
        console.log(`📦 Batch contains:`, options.specificDocuments.map(doc => doc.title));
        documents = options.specificDocuments.map(doc => ({
          id: doc.id,
          attributes: {
            title: doc.title,
            fileUrl: doc.fileUrl,
            mimeType: doc.mimeType,
            fileSize: doc.fileSize
          }
        }));
      } else {
        // Fallback: fetch all documents from collection (NOT recommended for batch processing)
        console.warn('⚠️ No batch specified! Falling back to ALL collection documents - this may cause incorrect results');
        const collectionDocs = await dmsService.getCollectionDocuments(collectionId);
        documents = collectionDocs;
      }
      
      if (!documents || documents.length === 0) {
        throw new Error('No documents found to process');
      }

      console.log(`📋 Batch processing ${documents.length} documents from current upload session:`, documents.map(doc => doc.attributes?.title || doc.title));

      // Step 3: Transform documents to workflow input structure
      const documentInputs = documents.map(doc => ({
        file_url: doc.attributes?.fileUrl || doc.fileUrl,
        metadata: {
          document_name: doc.attributes?.title || doc.title,
          mime_type: doc.attributes?.mimeType || doc.mimeType,
          file_size: doc.attributes?.fileSize || doc.fileSize,
          document_id: doc.id
        }
      }));

      // Step 4: Build inputs for TurfAI public API format
      const inputs = {
        // Send documents in the format TurfAI expects
        documents: documentInputs,
        processing_metadata: {
          employee_id: employeeData.employee_id,
          full_name: employeeData.full_name,
          department: employeeData.department,
          joiner_type: employeeData.joiner_type,
          experience_level: employeeData.experience_level,
          context: 'employee_onboarding',
          collection_id: collectionId,
          timestamp: new Date().toISOString()
        },
        processing_options: {
          parallel_processing: options?.parallel || true,
          max_concurrent: options?.maxConcurrent || 5,
          timeout_seconds: options?.timeout || 300,
          retry_on_failure: options?.retry || true
        }
      };

      // Step 5: Execute workflow using TurfAI public API format
      const executeEndpoint = `${this.baseUrl}${turfaiConfig.workflow.executeEndpoint}`;

      // Process first document (single document workflow)
      const firstDocument = documentInputs[0];
      
      // Build inputs based on workflow type
      const workflowInputs: any = {
        file_url: firstDocument.file_url,
        metadata: {
          document_name: firstDocument.metadata.document_name,
          mime_type: firstDocument.metadata.mime_type,
          file_size: firstDocument.metadata.file_size,
          document_id: firstDocument.metadata.document_id,
          employee_id: employeeData.employee_id,
          full_name: employeeData.full_name,
          // Workflow execution metadata
          workflow_type: workflowType,
          workflow_definition: resolvedWorkflow.definition,
          execution_environment: resolvedWorkflow.environment,
          department: employeeData.department,
          collection_id: collectionId,
          processing_context: 'employee_onboarding'
        }
      };
      
      // For pure extraction workflows, add prompt data
      if (workflowType === 'system-extract' || workflowType === 'extract') {
        // Use provided prompt or default
        if (options?.promptId) {
          workflowInputs.prompt_id = options.promptId;
          workflowInputs.prompt_data = {
            title: options.promptText || 'Custom Extraction',
            description: 'Extract data based on provided prompt',
            tasks: ['Extract data as specified'],
            instructions: ['Return extracted data as structured JSON']
          };
        } else {
          // Default generic prompt
          workflowInputs.prompt_id = 'generic_extraction';
          workflowInputs.prompt_data = {
            title: 'Generic Document Extraction',
            description: 'Extract all text and structured data from document',
            tasks: ['Extract all visible text', 'Identify document structure', 'Extract key-value pairs'],
            instructions: ['Return extracted data as structured JSON']
          };
        }
      }
      
      const response = await fetch(executeEndpoint, {
        method: 'POST',
        headers: authService.getDualAuthHeaders(), // Use dual auth headers
        body: JSON.stringify({
          activityId: resolvedWorkflow.id, // Use resolved workflow ID
          // API workflow format - double nested inputs for schema validation
          inputs: {
            inputs: workflowInputs
          }
        })
      });

      if (!response.ok) {
        let errorMessage = `Failed to execute workflow: ${response.status} ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage += ` - ${errorData.error.message}`;
            
            if (response.status === 401) {
              errorMessage += '\n\n🔑 API Key Issue: Please ensure you have set VITE_TURFAI_API_KEY in your .env file with a valid API key from the TurfAI platform.';
            } else if (response.status === 500) {
              errorMessage += '\n\n🔧 Server Error: There was an internal server error. Please check TurfAI service logs and try again.';
            } else if (response.status === 404) {
              errorMessage += '\n\n📍 Not Found: The workflow or endpoint was not found. Please check if TurfAI services are running properly.';
            }
          }
        } catch (e) {
          // If response is not JSON, try as text
          try {
            const errorText = await response.text();
            errorMessage += ` - ${errorText}`;
            
            if (response.status === 500) {
              errorMessage += '\n\n🔧 Server Error: Internal server error occurred. Check TurfAI service logs for details.';
            }
          } catch (textError) {
            if (response.status === 500) {
              errorMessage += '\n\n🔧 Server Error: Internal server error occurred. Unable to get error details.';
            }
          }
        }
        
        throw new Error(errorMessage);
      }

      const execution = await response.json();

      // Return in our expected format
      return {
        id: execution.id,
        attributes: {
          status: execution.status || 'queued',
          outputs: execution.outputs || null,
          activity: this.workflowId,
          workflow_definition: null as any,
          inputs: inputs
        }
      };
    } catch (error) {
      console.error('Error executing batch classification:', error);
      throw error;
    }
  }

  async getValidationReport(executionId: string): Promise<{
    validation_results: ValidationResults;
    gap_analysis: GapAnalysis;
    approval_recommendation: any;
  }> {
    try {
      const execution = await this.getExecutionStatus(executionId);
      
      // The public API returns outputs at root level
      const outputs = execution.outputs || execution.attributes?.outputs;
      
      if (!outputs) {
        throw new Error('Workflow execution has no outputs yet');
      }

      // Handle the actual output structure from TurfAI workflow
      // Outputs contain dynamic node IDs like "node-1754554357593"
      // Get the first (and likely only) node result
      const nodeKeys = Object.keys(outputs);
      const firstNodeKey = nodeKeys[0];
      const nodeResult = firstNodeKey ? outputs[firstNodeKey] : {};
      
      
      // Extract results from the node structure
      const extractionResult = nodeResult.extraction_result || {};
      
      // Transform to expected format for backward compatibility
      // Create document results array for the dashboard
      // const documentResults = [{
      //   document_name: nodeResult.metadata?.document_name || 'Unknown Document',
      //   success: true,
      //   status: 'completed',
      //   confidence: extractionResult.confidence || 0,
      //   classification: {
      //     document_type: extractionResult.classification || extractionResult.prompt_title || 'Unknown',
      //     prompt_title: extractionResult.prompt_title,
      //     section: extractionResult.section
      //   }
      // }];
      
      return {
        validation_results: {
          overall_score: extractionResult.confidence || 0,
          document_scores: {},
          completeness_score: 1, // Since extraction succeeded
          accuracy_score: extractionResult.confidence || 0,
          consistency_score: 1,
          extraction_data: extractionResult,
          classification_data: extractionResult,
          // Include the full node result for debugging
          raw_node_result: nodeResult
        },
        gap_analysis: {
          missing_documents: [],
          invalid_documents: [],
          recommendations: []
        },
        approval_recommendation: {
          approved: true,
          confidence: extractionResult.confidence || 0,
          reasoning: extractionResult.reasoning || 'Document successfully processed',
          document_type: extractionResult.classification || extractionResult.prompt_title || 'Unknown',
          prompt_title: extractionResult.prompt_title || 'Unknown',
          recommendation: extractionResult.confidence >= 0.9 ? 'auto_approve' : 'manual_review',
          recommendation_reason: extractionResult.reasoning || 'Document classification completed',
          action_items: [],
          requires_action: false
        }
      };
    } catch (error) {
      console.error('Error getting validation report:', error);
      throw error;
    }
  }

  async startWorkflowExecution(executionId: string): Promise<WorkflowExecution> {
    try {
      const response = await fetch(`${this.baseUrl}/workflow-executions/${executionId}/execute`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to start workflow execution: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<WorkflowExecution>;
      return result.data;
    } catch (error) {
      console.error('Error starting workflow execution:', error);
      throw error;
    }
  }

  async getExecutionStatus(executionId: string): Promise<WorkflowExecution> {
    try {
      // Use TurfAI public API status endpoint - fix the URL structure
      const statusEndpoint = `${this.baseUrl}${turfaiConfig.workflow.statusEndpoint}/${executionId}/status`;
      
      const response = await fetch(statusEndpoint, {
        headers: authService.getDualAuthHeaders() // Use dual auth headers
      });

      if (!response.ok) {
        let errorMessage = `Failed to fetch execution status: ${response.status} ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage += ` - ${errorData.error.message}`;
            
            if (response.status === 403) {
              errorMessage += '\n\n🔒 Access Denied: This workflow execution may belong to a different user or session. Please restart the workflow.';
            }
          }
        } catch (e) {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        }
        
        throw new Error(errorMessage);
      }

      const execution = await response.json();
      
      // Return the status response as-is since it already has the correct structure
      // The public API returns outputs at root level, not wrapped in attributes
      return execution as WorkflowExecution;
    } catch (error) {
      console.error('Error fetching execution status:', error);
      throw error;
    }
  }

  async resumeWorkflow(executionId: string, corrections: any): Promise<WorkflowExecution> {
    try {
      const response = await fetch(`${this.baseUrl}/workflow-executions/${executionId}/resume`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          correlation_token: corrections.correlation_token,
          payload: corrections
        })
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to resume workflow: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<WorkflowExecution>;
      return result.data;
    } catch (error) {
      console.error('Error resuming workflow:', error);
      throw error;
    }
  }

  async cancelWorkflowExecution(executionId: string): Promise<WorkflowExecution> {
    try {
      const response = await fetch(`${this.baseUrl}/workflow-executions/${executionId}/cancel`, {
        method: 'POST',
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to cancel workflow: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<WorkflowExecution>;
      return result.data;
    } catch (error) {
      console.error('Error cancelling workflow:', error);
      throw error;
    }
  }

  // Helper methods to extract results
  extractValidationResults(execution: WorkflowExecution): ValidationResults | null {
    const outputs = execution.attributes?.outputs;
    if (!outputs) return null;

    // Try different possible output structures
    const validationResults = outputs.validation_results || 
                            outputs.scoring_results || 
                            outputs.validation;

    if (!validationResults) return null;

    return {
      overall_score: validationResults.overall_score || 0,
      document_scores: validationResults.document_scores || {},
      completeness_score: validationResults.completeness_score || 0,
      accuracy_score: validationResults.accuracy_score || 0,
      consistency_score: validationResults.consistency_score || 0
    };
  }

  extractGapAnalysis(execution: WorkflowExecution): GapAnalysis | null {
    const outputs = execution.attributes?.outputs;
    if (!outputs) return null;

    const gapAnalysis = outputs.gap_analysis || outputs.gaps || outputs.analysis;
    if (!gapAnalysis) return null;

    return {
      missing_documents: gapAnalysis.missing_documents || [],
      invalid_documents: gapAnalysis.invalid_documents || [],
      recommendations: gapAnalysis.recommendations || []
    };
  }

  extractExtractedData(execution: WorkflowExecution): any {
    const outputs = execution.attributes?.outputs;
    if (!outputs) return {};

    return outputs.extracted_data || 
           outputs.extraction_results || 
           outputs.document_data || 
           {};
  }

  // Polling helper for execution status
  async pollExecutionStatus(
    executionId: string, 
    onUpdate?: (execution: WorkflowExecution) => void,
    pollInterval: number = 2000
  ): Promise<WorkflowExecution> {
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const execution = await this.getExecutionStatus(executionId);
          
          if (onUpdate) {
            onUpdate(execution);
          }

          const status = execution.attributes?.status;
          
          if (status === 'completed' || status === 'failed' || status === 'cancelled') {
            resolve(execution);
          } else {
            setTimeout(checkStatus, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };

      checkStatus();
    });
  }

  // Get workflow execution history
  async getExecutionHistory(filters?: {
    collectionId?: string;
    employeeId?: string;
    status?: string;
  }): Promise<WorkflowExecution[]> {
    try {
      let url = `${this.baseUrl}/workflow-executions?populate=*`;
      
      if (filters?.collectionId) {
        url += `&filters[inputs][collection_id][$eq]=${filters.collectionId}`;
      }
      if (filters?.employeeId) {
        url += `&filters[inputs][employee_data][employee_id][$eq]=${filters.employeeId}`;
      }
      if (filters?.status) {
        url += `&filters[status][$eq]=${filters.status}`;
      }

      const response = await fetch(url, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to fetch execution history: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<WorkflowExecution[]>;
      return result.data;
    } catch (error) {
      console.error('Error fetching execution history:', error);
      throw error;
    }
  }

  // Check if workflow service is available
  async checkConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/workflow-executions`, {
        method: 'GET',
        headers: authService.getAuthHeaders()
      });
      return response.ok;
    } catch (error) {
      console.error('Workflow service connection check failed:', error);
      return false;
    }
  }

  // Validate workflow resolution health
  async validateWorkflowHealth(): Promise<{ valid: boolean; errors: string[]; details: any }> {
    const result = await workflowResolver.validateWorkflowRegistry();
    
    return {
      valid: result.valid,
      errors: result.errors,
      details: {
        availableWorkflows: workflowResolver.getAvailableWorkflows().length,
        singleStageWorkflows: workflowResolver.getWorkflowsByType('single-stage').length,
        multiStageWorkflows: workflowResolver.getWorkflowsByType('multi-stage').length,
        currentEnvironment: workflowResolver['environment']
      }
    };
  }

  // Get workflow information for debugging
  getWorkflowInfo(): {
    available: { logicalName: string; definition: any }[];
    singleStage: { logicalName: string; definition: any }[];
    multiStage: { logicalName: string; definition: any }[];
  } {
    return {
      available: workflowResolver.getAvailableWorkflows(),
      singleStage: workflowResolver.getWorkflowsByType('single-stage'),
      multiStage: workflowResolver.getWorkflowsByType('multi-stage')
    };
  }
}

// Export a singleton instance
export const workflowService = new TurfAIWorkflowService();